import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ForgotPasswordPage = () => {
  const { t, language } = useLanguage();
  const isRTL = language === 'ar';
  const [identifier, setIdentifier] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    if (!identifier.trim()) {
      setError(t('الرجاء إدخال البريد الإلكتروني أو رقم الهاتف', 'Please enter your email or phone', 'Champ requis'));
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { identifier: identifier.trim() });
      setMessage(t(
        'إذا كان الحساب موجوداً، فسنرسل رابط إعادة تعيين كلمة المرور إلى بريدك الإلكتروني.',
        'If an account exists, a password reset link has been sent to your email.',
        'Si un compte existe, un lien de réinitialisation a été envoyé.'
      ));
    } catch (err) {
      setError(err.response?.data?.detail || t('حدث خطأ. حاول لاحقاً', 'An error occurred. Please try again.', 'Erreur, réessayez.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`min-h-screen bg-gradient-to-b from-blue-50 to-white flex items-center justify-center px-4 py-8 ${isRTL ? 'rtl' : 'ltr'}`}>
      <Card className="w-full max-w-md p-8" data-testid="forgot-password-card">
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-[#2c3e50] mb-2">
            {t('نسيت كلمة المرور', 'Forgot Password', 'Mot de passe oublié')}
          </h1>
          <p className="text-gray-600 text-sm">
            {t(
              'أدخل بريدك الإلكتروني أو رقم هاتفك وسنرسل لك رابط إعادة تعيين كلمة المرور',
              'Enter your email or phone number and we will send you a reset link',
              'Entrez votre e-mail ou téléphone pour recevoir un lien'
            )}
          </p>
        </div>

        {error && (
          <Alert variant="destructive" className="mb-4" data-testid="forgot-error-alert">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        {message && (
          <Alert className="mb-4 bg-green-50 border-green-200" data-testid="forgot-success-alert">
            <AlertDescription className="text-green-800">{message}</AlertDescription>
          </Alert>
        )}

        <form onSubmit={handleSubmit} className="space-y-5" data-testid="forgot-form">
          <div>
            <Label htmlFor="identifier">
              {t('البريد الإلكتروني أو رقم الهاتف', 'Email or Phone Number', 'E-mail ou téléphone')}
            </Label>
            <Input
              id="identifier"
              type="text"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              placeholder="email@example.com or +961..."
              required
              data-testid="identifier-input"
              className="mt-2"
            />
          </div>
          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-[#1e88e5] hover:bg-[#1976d2]"
            data-testid="forgot-submit-button"
          >
            {loading ? t('جاري الإرسال...', 'Sending...', 'Envoi...') : t('إرسال رابط الإعادة', 'Send Reset Link', 'Envoyer')}
          </Button>
        </form>

        <div className="mt-6 text-center">
          <Link to="/login" className="text-sm text-[#1e88e5] hover:underline" data-testid="back-to-login-link">
            {t('العودة إلى تسجيل الدخول', 'Back to login', 'Retour à la connexion')}
          </Link>
        </div>
      </Card>
    </div>
  );
};

export default ForgotPasswordPage;
